import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { WafDataGrid } from '../components/WafDataGrid';
import { CrStatusChip } from '../components/CrStatusChip';
import { LoadingScreen } from '../components/LoadingScreen';
import {
  confirmEntrantDetails,
  getEntrantMe,
  type EntrantEntry,
  type EntrantSession,
} from '../lib/api';

/**
 * Landing page for authenticated entrants. Shows a DataGrid of every
 * nomination they have access to (one entrant can be granted multiple).
 * Clicking a row navigates to /submit-change with the selected
 * nomination_uuid in router state.
 */
export function EntrantHome() {
  const navigate = useNavigate();
  const [session, setSession] = useState<EntrantSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getEntrantMe();
        setSession(data);
      } catch (err) {
        setError(extractApiError(err, 'Unable to load your entries.'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <LoadingScreen />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!session) return <Alert severity="error">Unable to load your entries.</Alert>;

  const entries = session.entries ?? [];
  const rows = entries.map((e) => ({ ...e, id: e.nomination_uuid }));

  const columns: GridColDef[] = [
    { field: 'nomination_code', headerName: 'Project Number', width: 160 },
    { field: 'project_title', headerName: 'Title', flex: 1.4, minWidth: 220 },
    { field: 'architect_company_name', headerName: 'Architect', flex: 1, minWidth: 180 },
    { field: 'category_name', headerName: 'Category', flex: 1, minWidth: 160 },
    { field: 'project_country', headerName: 'Country', width: 140 },
    {
      field: 'change_requests',
      headerName: 'Status',
      width: 180,
      sortable: false,
      renderCell: (p: GridRenderCellParams<EntrantEntry>) => (
        <CrStatusChip cr={p.row.change_requests} />
      ),
    },
  ];

  return (
    <Stack spacing={3}>
      <Typography
        variant="h3"
        component="h1"
        align="center"
        sx={{ wordBreak: 'break-word' }}
      >
        Welcome, {session.entrant.name}
      </Typography>

      <YourDetailsCard contact={session.contact} />

      {entries.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No entries linked to your account yet. The WAF admin team will grant access shortly.
          </Typography>
        </Box>
      ) : (
        <Paper>
          <WafDataGrid
            rows={rows}
            columns={columns}
            density="standard"
            onRowClick={(p) =>
              navigate('/submit-change', {
                state: { nomination_uuid: (p.row as EntrantEntry).nomination_uuid },
              })
            }
            pageSizeOptions={[10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </Paper>
      )}
    </Stack>
  );
}

/**
 * "Your details" card: the entrant reviews / edits their primary contact
 * details and confirms them. Edits go in as change requests; confirming
 * flips the status chip to green. Local state is seeded from the session's
 * `contact` object and stays controlled from there.
 */
function YourDetailsCard({ contact }: { contact: EntrantSession['contact'] }) {
  const [name, setName] = useState(contact.name ?? '');
  const [practiceName, setPracticeName] = useState(contact.practice_name ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  const [phone, setPhone] = useState(contact.phone ?? '');

  const [confirmed, setConfirmed] = useState(contact.confirmed);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await confirmEntrantDetails({
        name,
        practice_name: practiceName,
        email,
        phone,
      });
      setConfirmed(res.confirmed);
      setNotice(
        res.created > 0
          ? `Details confirmed. ${res.created} change ${res.created === 1 ? 'request' : 'requests'} submitted for review.`
          : 'Details confirmed. Thank you.'
      );
    } catch (err) {
      setError(extractApiError(err, 'Could not confirm your details. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
        >
          <Typography variant="h5" component="h2">
            Your details
          </Typography>
          {confirmed ? (
            <Chip color="success" icon={<CheckCircleIcon />} label="Confirmed" />
          ) : (
            <Chip color="warning" label="Unconfirmed — please review" />
          )}
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Please check your primary contact details below, make any corrections, then click
          “Confirm details”. Edits are submitted to the WAF admin team for review.
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Primary contact"
            size="small"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={submitting}
          />
          <TextField
            label="Primary practice name"
            size="small"
            fullWidth
            value={practiceName}
            onChange={(e) => setPracticeName(e.target.value)}
            disabled={submitting}
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Primary email address"
            type="email"
            size="small"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
          />
          <TextField
            label="Primary telephone number"
            size="small"
            fullWidth
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={submitting}
          />
        </Stack>

        <Box>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={submitting}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {submitting ? 'Confirming…' : 'Confirm details'}
          </Button>
        </Box>
      </Stack>

      <Snackbar
        open={notice !== null}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ width: '100%' }}>
          {notice}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

function extractApiError(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: { message?: string; error?: string } } }).response
      ?.data;
    if (data?.message) return data.message;
    if (data?.error) return data.error;
  }
  return err instanceof Error ? err.message : fallback;
}
